import { settingsButton } from '@tma.js/sdk-react'
import { useEffect } from 'react'
import { useLatest } from '@/shared/lib'

type Handler = () => void

/**
 * Пункт «Настройки» в меню ⋮ Mini App — его рисует сам Telegram, своей
 * иконки в шапке нет (решение пользователя 18.10.2026).
 *
 * Как и BackButton, кнопка одна, а управляющих двое: оболочка задаёт, куда она
 * ведёт (`useSettingsButton`; на самом экране настроек — `null`, пункта нет), а
 * экран может её спрятать на время действия, которое нельзя бросать посередине
 * (`useHideSettingsButton`, — например «Оплатить», пока создаётся заказ).
 *
 * Клиент старше 6.10 кнопки не знает: вызовы молча не срабатывают, экран
 * настроек остаётся доступен только по адресу.
 */
let target: Handler | null = null
let hiddenBy = 0

const isActive = (): boolean => target !== null && hiddenBy === 0

const sync = (): void => {
  if (isActive()) {
    settingsButton.show.ifAvailable()
  } else {
    settingsButton.hide.ifAvailable()
  }
}

/** Подписка на нажатие — один раз при инициализации SDK. */
export const bindSettingsButton = (): void => {
  settingsButton.onClick.ifAvailable(() => {
    if (isActive()) {
      target?.()
    }
  })
}

/** Куда ведёт пункт «Настройки». Только для оболочки приложения; `null` — пункта нет. */
export const useSettingsButton = (handler: Handler | null): void => {
  const latest = useLatest(handler)
  const enabled = handler !== null

  useEffect(() => {
    target = enabled ? () => latest.current?.() : null
    sync()

    return () => {
      target = null
      sync()
    }
  }, [enabled, latest])
}

/** Спрятать пункт «Настройки», пока `isHidden`: уход с экрана сорвал бы начатое действие. */
export const useHideSettingsButton = (isHidden: boolean): void => {
  useEffect(() => {
    if (!isHidden) {
      return
    }

    hiddenBy += 1
    sync()

    return () => {
      hiddenBy -= 1
      sync()
    }
  }, [isHidden])
}
